'use client'

import '@/utils/lexical/index.css'

import {
  Dispatch,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'

import ExcalidrawPlugin from '@/utils/lexical/plugins/ExcalidrawPlugin'
import CodeActionMenuPlugin from '@/utils/lexical/plugins/CodeActionMenuPlugin'
import CollapsiblePlugin from '@/utils/lexical/plugins/CollapsiblePlugin'
import DraggableBlockPlugin from '@/utils/lexical/plugins/DraggableBlockPlugin'
import EquationsPlugin from '@/utils/lexical/plugins/EquationsPlugin'
import FloatingLinkEditorPlugin from '@/utils/lexical/plugins/FloatingLinkEditorPlugin'
import FloatingTextFormatToolbarPlugin from '@/utils/lexical/plugins/FloatingTextFormatToolbarPlugin'
import ImagesPlugin from '@/utils/lexical/plugins/ImagesPlugin'
import KeywordsPlugin from '@/utils/lexical/plugins/KeywordsPlugin'
import MarkdownPlugin from '@/utils/lexical/plugins/MarkdownShortcutPlugin'
import TableCellActionMenuPlugin from '@/utils/lexical/plugins/TableActionMenuPlugin'
import ToolbarPlugin from '@/utils/lexical/plugins/ToolbarPlugin'
import YouTubePlugin from '@/utils/lexical/plugins/YouTubePlugin'
import AutoLinkPlugin from '@/utils/lexical/plugins/AutoLinkPlugin'

import PlaygroundNodes from '@/utils/lexical/nodes/PlaygroundNodes'
import { CAN_USE_DOM } from '@/utils/lexical/shared/canUseDOM'
import ContentEditable from '@/utils/lexical/ui/ContentEditable'

import PlaygroundEditorTheme from '@/utils/lexical/themes/PlaygroundEditorTheme'
import AutoEmbedPlugin from '@/utils/lexical/plugins/AutoEmbedPlugin'
import { EditorState, LexicalEditor } from 'lexical'

const theme = {
  //   // Theme styling goes here
  ...PlaygroundEditorTheme,
}

export const defaultEditorStateString =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'

// Lexical React plugins are React components, which makes them
// highly composable. Furthermore, you can lazy load plugins if
// desired, so you don't pay the cost for plugins until you
// actually use them.
function MyCustomAutoFocusPlugin() {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Wrap in setTimeout to ensure DOM is ready
    const timer = setTimeout(() => {
      // Focus the editor when the effect fires!
      editor.focus()
    }, 0)
    return () => clearTimeout(timer)
  }, [editor])

  return null
}

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: unknown) {
  console.error(error)
}

const EditorCapturePlugin = forwardRef<LexicalEditor | null>((_, ref) => {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    if (!ref) return // Ensure ref is not null before using it

    if (typeof ref === 'function') {
      ref(editor)
    } else {
      ;(ref as React.RefObject<LexicalEditor | null>).current = editor
    }

    return () => {
      if (typeof ref === 'function') {
        ref(null) // Important: reset ref on unmount
      } else {
        ;(ref as React.RefObject<LexicalEditor | null>).current = null
      }
    }
  }, [editor, ref])

  return null
})

interface InitializeContentPluginProps {
  value: string
  isInitialLoad: boolean
  setIsInitialLoad: Dispatch<SetStateAction<boolean>>
  isEditable?: boolean
}

const InitializeContentPlugin = ({
  value,
  isInitialLoad,
  setIsInitialLoad,
  isEditable,
}: InitializeContentPluginProps) => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    let isActive = true

    if (isInitialLoad && value) {
      const timeoutId = setTimeout(() => {
        if (!isActive) return

        editor.update(() => {
          try {
            const editorState = editor.parseEditorState(value)
            editor.setEditorState(editorState)
          } catch (error) {
            console.error('Error parsing editor state:', error)
          }
        })

        setIsInitialLoad(false)
      }, 0)

      return () => {
        isActive = false
        clearTimeout(timeoutId)
      }
    }
  }, [value, editor, isInitialLoad, setIsInitialLoad])

  useEffect(() => {
    if (!isEditable) {
      setIsInitialLoad(true)
    }
  }, [value, isEditable, setIsInitialLoad])

  return <></>
}

const Placeholder = ({ children }) => (
  <div className="text-md text-v8-mut overflow-hidden absolute text-ellipsis select-none whitespace-nowrap inline-block pointer-events-none top-2 inset-x-7">
    {children}
  </div>
)

interface EditorProps {
  onChange?: (value?: string) => void
  value: string
  isEditable?: boolean
  showPlaceholder?: boolean
  minHeight?: boolean
  additionalImageStyles?: string
  shellClassName?: string
}

export const Editor = forwardRef<LexicalEditor, EditorProps>(
  (
    {
      onChange,
      value,
      isEditable = true,
      showPlaceholder = true,
      minHeight = false,
      additionalImageStyles = '',
      shellClassName = 'editor-shell relative w-full my-0',
    },
    ref,
  ) => {
    const [floatingAnchorElem, setFloatingAnchorElem] =
      useState<HTMLDivElement | null>(null)
    const [isSmallWidthViewport, setIsSmallWidthViewport] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    const initialConfig = useMemo(
      () => ({
        namespace: 'MyEditor',
        theme: {
          ...theme,
          image: `${theme.image} ${
            !!additionalImageStyles ? additionalImageStyles : ''
          }`,
        },
        nodes: [...PlaygroundNodes],
        editable: isEditable,
        onError,
      }),
      [additionalImageStyles, isEditable],
    )

    // Use useCallback, to prevent function reference changes
    const onRef = useCallback((_floatingAnchorElem: HTMLDivElement) => {
      if (_floatingAnchorElem !== null) {
        setFloatingAnchorElem(_floatingAnchorElem)
      }
    }, [])

    useEffect(() => {
      const updateViewPortWidth = () => {
        const isNextSmallWidthViewport =
          CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches

        setIsSmallWidthViewport((prev) => {
          if (prev !== isNextSmallWidthViewport) {
            return isNextSmallWidthViewport
          }
          return prev
        })
      }

      // call once on component mount
      updateViewPortWidth()

      window.addEventListener('resize', updateViewPortWidth)

      return () => {
        window.removeEventListener('resize', updateViewPortWidth)
      }
    }, [])

    // Use useRef for onChange, to prevent onEditorChange re-creation on prop change
    const onChangeRef = useRef(onChange)
    useEffect(() => {
      onChangeRef.current = onChange
    }, [onChange])

    // When the editor changes, you can get notified via the
    // LexicalOnChangePlugin!
    const onEditorChange = useCallback((editorState: EditorState) => {
      const editorStateString = JSON.stringify(editorState)
      if (editorStateString === defaultEditorStateString) return

      // Call the current (actual) version of onChange through the ref
      if (onChangeRef.current) {
        editorState.read(() => {
          // Check for null to prevent errors
          onChangeRef.current?.(editorStateString)
        })
      }
    }, [])

    return (
      <div className={shellClassName}>
        <LexicalComposer initialConfig={initialConfig}>
          {isEditable ? <ToolbarPlugin /> : <></>}
          <div className="editor-container">
            <KeywordsPlugin />
            {ref && <EditorCapturePlugin ref={ref} />}
            <RichTextPlugin
              contentEditable={
                <div
                  className={`editor-scroller ${
                    minHeight ? 'unset-height' : ''
                  }`}
                >
                  <div className="editor" ref={onRef}>
                    <ContentEditable />
                  </div>
                </div>
              }
              placeholder={
                showPlaceholder ? <Placeholder>Type here</Placeholder> : null
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <InitializeContentPlugin
              value={value}
              isInitialLoad={isInitialLoad}
              isEditable={isEditable}
              setIsInitialLoad={setIsInitialLoad}
            />
            <OnChangePlugin onChange={onEditorChange} />
            <AutoEmbedPlugin />
            <AutoLinkPlugin />
            <HistoryPlugin />
            <TablePlugin />
            <EquationsPlugin />
            <MyCustomAutoFocusPlugin />
            <HorizontalRulePlugin />
            <ImagesPlugin />
            <CollapsiblePlugin />
            <MarkdownPlugin />
            <ExcalidrawPlugin />
            <YouTubePlugin />
            {floatingAnchorElem && !isSmallWidthViewport && (
              <>
                <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
                <CodeActionMenuPlugin anchorElem={floatingAnchorElem} />
                <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
                <TableCellActionMenuPlugin
                  anchorElem={floatingAnchorElem}
                  cellMerge={true}
                />
                <FloatingTextFormatToolbarPlugin
                  anchorElem={floatingAnchorElem}
                />
              </>
            )}
          </div>
        </LexicalComposer>
      </div>
    )
  },
)
Editor.displayName = 'Editor'
EditorCapturePlugin.displayName = 'EditorCapturePlugin'
